/** Tones, thresholds and small number helpers shared by the GPU pods and backend tabs. */

export type Tone = "ok" | "warn" | "nok" | "neutral";

/** value ink of a tone: problems in colour, everything else stays in the foreground */
export const toneText = (tone: Tone | undefined) =>
  tone === "nok" ? "text-red-300" : tone === "warn" ? "text-amber-300" : "";

export const worstTone = (...tones: Tone[]): Tone =>
  tones.includes("nok") ? "nok" : tones.includes("warn") ? "warn" : tones.includes("ok") ? "ok" : "neutral";

/** thresholds of the backend findings (pizza-backend src/monitoring/findings.py) */
export const LIMITS = {
  lagWarnMs: 300,
  lagNokMs: 1000,
  podSlowMs: 2500,
  podFailureRate: 0.1,
  podFailuresMin: 3,
  podQueueWarn: 5,
  unhealthyAfterFailures: 3,
  dependencySlowMs: 1500,
  backgroundWritesBacklog: 50,
  recentRestartS: 600,
  /** an error / failure younger than this still counts as current */
  recentMs: 15 * 60_000,
  /** a monitoring flush older than this means the flush loop is stuck */
  flushStaleS: 60,
  // from the pods' own GET /metrics
  podGpuHotC: 85,
  podGpuMemoryHigh: 0.95,
  /** the crust inference alone blocks a pod's loop ~150-250 ms */
  podLoopLagMs: 1000,
  podServerErrorsMin: 3,
} as const;

/** span of the backend's in-memory sample rings (lag probe, process sampler) */
export const HOUR_MS = 3_600_000;

export const lagTone =(ms: number | null | undefined): Tone =>
  ms === null || ms === undefined ? "neutral" : ms >= LIMITS.lagNokMs ? "nok" : ms >= LIMITS.lagWarnMs ? "warn" : "neutral";

/** failure share of attempts, toned like the pod_errors finding (and red past one half) */
export function failureTone(failures: number, attempts: number): Tone {
  if (!attempts || failures < LIMITS.podFailuresMin) return "neutral";
  const rate = failures / attempts;
  if (rate >= 0.5) return "nok";
  return rate >= LIMITS.podFailureRate ? "warn" : "neutral";
}

/** value of a sorted array at quantile q (nearest rank) */
export function quantile(sorted: number[], q: number): number | null {
  if (!sorted.length) return null;
  const index = Math.min(sorted.length - 1, Math.max(0, Math.ceil(q * sorted.length) - 1));
  return sorted[index];
}

/** epoch ms of a timestamp that may arrive in seconds (the backend's raw process sample) */
export const toEpochMs = (ts: number | null | undefined): number | null =>
  ts === null || ts === undefined ? null : ts < 1e12 ? ts * 1000 : ts;

export const isRecent = (ts: number | null | undefined, now: number, ms: number = LIMITS.recentMs) =>
  !!ts && now - ts < ms;
